// SPDX-License-Identifier: LicenseRef-Proprietary
import React, { useState, useEffect, useRef } from "react";
import { logActivity } from "../lib/storage";

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (n: string) => void;
  suffix?: string;
  min?: number;
  step?: number;
  max?: number;
  disabled?: boolean;
  "aria-label"?: string;
  hideLabel?: boolean;
  debounceMs?: number;
}

export const TextField: React.FC<TextFieldProps> = ({
  label,
  value,
  onChange,
  suffix,
  disabled = false,
  "aria-label": ariaLabel,
  hideLabel = false,
  debounceMs = 2000,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUserTypingRef = useRef(false);
  const lastExternalValueRef = useRef(value); // Track last external value to detect external changes

  const prevValueRef = useRef(value);

  useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value;
    }

    // Detect if this is an external change (not from user typing)
    const isExternalChange = lastExternalValueRef.current !== value;
    if (isExternalChange) {
      lastExternalValueRef.current = value;
    }

    // CRITICAL: Clear any pending debounce timeout when value prop changes externally
    // This prevents stale onChange calls from overwriting the new value
    // (e.g., when loading a scenario from the store, or when resetting to defaults)
    // This must happen BEFORE updating localValue to prevent race conditions
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      // Reset typing flag since external change means user is no longer typing
      isUserTypingRef.current = false;
    }

    // Always sync localValue with value prop when it changes externally
    // This ensures the input reflects external state changes (reset, load scenario, etc.)
    // Force update if this is an external change, even if values appear the same
    // (handles edge cases like empty string vs undefined)
    setLocalValue((prev) => {
      if (isExternalChange || prev !== value) {
        isUserTypingRef.current = false;
        return value;
      }
      return prev;
    });
  }, [value, label]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    
    // Check if this change should be ignored (external value changed while user was typing)
    const currentExternalValue = lastExternalValueRef.current;
    if (currentExternalValue !== value) {
      // Sync back to external value
      setLocalValue(currentExternalValue);
      return;
    }
    
    isUserTypingRef.current = true;
    setLocalValue(newValue);

    if (debounceMs > 0) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      // Capture the external value at the time of scheduling to compare later
      const externalValueAtScheduleTime = lastExternalValueRef.current;
      timeoutRef.current = setTimeout(() => {
        // CRITICAL: Check if external value has changed since we scheduled this callback
        // If it has changed, it means an external update (reset, load scenario) happened
        // and we should NOT overwrite it with the old user input
        const currentExternalValue = lastExternalValueRef.current;
        if (currentExternalValue === externalValueAtScheduleTime) {
          // External value hasn't changed, safe to call onChange
          onChange(newValue);
          logActivity(`Updated text field': ${label}, value: ${newValue}`);
          isUserTypingRef.current = false;
        } else {
          isUserTypingRef.current = false;
        }
      }, debounceMs);
    } else {
      onChange(newValue);
      logActivity(`Updated text field': ${label}, value: ${newValue}`);
      isUserTypingRef.current = false;
    }
  };

  return (
    <div className="vaccine-demo__text-field">
      {!hideLabel && (
        <label
          className="vaccine-demo__label"
          htmlFor={`input-${label.replace(/\s+/g, "-").toLowerCase()}`}
        >
          {label}
        </label>
      )}
      <div className="vaccine-demo__input-group">
        <input
          id={`input-${label.replace(/\s+/g, "-").toLowerCase()}`}
          type="text"
          value={localValue}
          disabled={disabled}
          onChange={handleChange}
          className="vaccine-demo__input"
          aria-label={ariaLabel || label}
          data-cy={ariaLabel ? `input-${ariaLabel.toLowerCase().replace(/\s+/g, "-")}` : undefined}
          placeholder={hideLabel ? label : undefined}
        />
        {suffix && (
          <span className="vaccine-demo__suffix" aria-hidden="true">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
};
